import { formatDate as formatDateIntl } from "@ews-admin/global-design-system";
import { toIntlLocale } from "./format.utils";
import { toLocalDateOnly } from "./promotion.utils";

// A LocalDate-only string ("YYYY-MM-DD", e.g. licence.startDate/endDate) represents
// a calendar date, not an instant. `new Date("2026-08-05")` parses it as UTC
// midnight; formatting/comparing that in a timezone behind UTC silently shifts it
// a day earlier (a licence created "today" showed yesterday's date). Force UTC on
// the shared formatter so the displayed calendar date always matches what the API
// actually sent, regardless of the viewer's timezone.
// The design-system formatter defaults to en-US, so the UI language is passed
// explicitly or French managers see English month names.
export function formatDateOnly(dateStr: string | null | undefined, language: string): string {
  if (!dateStr) return "—";
  return formatDateIntl(dateStr, { timeZone: "UTC", month: "short", locale: toIntlLocale(language) });
}

export function formatBillingPeriod(
  billingPeriod: string | null | undefined,
  locale: string,
  periodMonths?: number | null
): string {
  if (!billingPeriod) return "—";
  const [year, month] = billingPeriod.split("-").map(Number);
  if (!year || !month) return "—";
  const start = new Date(year, month - 1, 1);
  const startLabel = start.toLocaleDateString(locale, { year: "numeric", month: "long" });
  if (!periodMonths || periodMonths <= 1) return startLabel;

  const end = new Date(year, month - 1 + (periodMonths - 1), 1);
  const endLabel = end.toLocaleDateString(locale, { year: "numeric", month: "long" });
  return `${startLabel} – ${endLabel}`;
}

// "other": a status this page does not know, shown as-is on a neutral badge.
export type InvoiceDisplayStatus = "paid" | "pending" | "overdue" | "cancelled" | "other";

// The API writes PENDING, SENT and PAID; OVERDUE and CANCELLED are not produced by any
// code path but may exist in data. PENDING and SENT are unpaid, and shown as overdue once
// their due date has passed (nothing flips them to OVERDUE in the database).
export function invoiceDisplayStatus(
  status: string | null | undefined,
  dueDate: string | null | undefined,
  today: string = toLocalDateOnly(new Date())
): InvoiceDisplayStatus {
  switch (status?.toUpperCase()) {
    case "PAID":
      return "paid";
    case "CANCELLED":
    case "CANCELED":
      return "cancelled";
    case "OVERDUE":
      return "overdue";
    case "PENDING":
    case "SENT":
      return dueDate && dueDate < today ? "overdue" : "pending";
    default:
      return "other";
  }
}

export function isInvoicePayable(displayStatus: InvoiceDisplayStatus): boolean {
  return displayStatus === "pending" || displayStatus === "overdue";
}
